import { createContext, useContext, useCallback, Suspense, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import lazyWithReload from '../../utils/lazyWithReload';
import DrawerLoadingFallback from '../common/DrawerLoadingFallback';
import { registerEntityOpener } from '../../utils/openEntity';
import { noProviderOpener } from '../../utils/drawerNoProvider';
import { useAuth } from '@host/auth';

const SharedListDetailDrawer = lazyWithReload(() => import('./SharedListDetailDrawer'), 'SharedListDetailDrawer');

/**
 * SharedListDrawerContext — URL-driven global drawer cho "danh sách chia sẻ phê duyệt".
 * Source of truth: query param `?sharedListId=<uuid>` → drawer mở/đóng.
 *
 * Vì sao URL-driven (KHÔNG local state ở card): card render trong chat LIVE → MessageList
 * re-render liên tục → local useState ở card bị reset → drawer tự đóng. URL param sống qua
 * re-render. Mirror ApprovalDrawerContext.
 *
 * Mounted ở main.jsx (top-level) → card BotCard / unfurl LinkUnfurl gọi openSharedList().
 */
const Ctx = createContext(null);

// Provider không nằm trên cây React của người gọi (hay gặp nhất: nội dung xem TRONG một
// drawer KHÁC) → lui về sổ đăng ký module-scope. Xem utils/drawerNoProvider.
const NO_PROVIDER = {
  openSharedList: noProviderOpener('sharedList'),
  closeSharedList: () => {},
  sharedListId: null,
};

export function SharedListDrawerProvider({ children }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const sharedListId = searchParams.get('sharedListId');
  const { user } = useAuth();

  const openSharedList = useCallback((id) => {
    if (!id) return;
    setSearchParams((prev) => {
      if (prev.get('sharedListId') === String(id)) return prev;
      const next = new URLSearchParams(prev);
      next.set('sharedListId', id);
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const closeSharedList = useCallback(() => {
    setSearchParams((prev) => {
      if (!prev.get('sharedListId')) return prev;
      const next = new URLSearchParams(prev);
      next.delete('sharedListId');
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Khai báo với kernel utils/openEntity: "loại này mở bằng POPUP" → link/card trong chat
  // trỏ tới bản ghi loại này bung popup tại chỗ, không nhảy trang chi tiết (trắng trang chờ chunk).
  useEffect(() => registerEntityOpener('sharedList', openSharedList), [openSharedList]);

  return (
    <Ctx.Provider value={{ openSharedList, closeSharedList, sharedListId }}>
      {children}
      {sharedListId && (
        <Suspense fallback={<DrawerLoadingFallback onClose={closeSharedList} />}>
          <SharedListDetailDrawer listId={sharedListId} currentUserId={user?.id} onClose={closeSharedList} />
        </Suspense>
      )}
    </Ctx.Provider>
  );
}

export function useSharedListDrawer() {
  return useContext(Ctx) || NO_PROVIDER;
}
